import { mergeTimeSeriesData } from './chartTimeSeries';
import { formatCustomRangeLabel } from './timeRange';

const PERIOD_LABELS = {
  lastHour: 'the last hour',
  today: 'today',
  yesterday: 'yesterday',
  last7days: 'the last 7 days',
  last30days: 'the last 30 days',
  last90days: 'the last 90 days',
  thisMonth: 'this month',
  allTime: 'all time',
  custom: 'the selected range',
};

const WIDER_PERIOD = {
  lastHour: 'today',
  today: 'last7days',
  yesterday: 'last7days',
  last7days: 'last30days',
  last30days: 'last90days',
  last90days: 'allTime',
  thisMonth: 'last30days',
  allTime: null,
  custom: null,
};

const WIDER_PERIOD_LABELS = {
  last7days: '7 days',
  last30days: '30 days',
  last90days: '90 days',
  allTime: 'All time',
};

export function getPeriodLabel(period, timeRange = null) {
  if (period === 'custom' && timeRange?.startDate && timeRange?.endDate) {
    return formatCustomRangeLabel(timeRange.startDate, timeRange.endDate);
  }
  return PERIOD_LABELS[period] || period;
}

export function getWiderPeriod(period) {
  return WIDER_PERIOD[period] ?? null;
}

export function getWiderPeriodLabel(period) {
  const wider = getWiderPeriod(period);
  return wider ? WIDER_PERIOD_LABELS[wider] || wider : null;
}

export function hasPlatformResources(statistics) {
  const assistantCount = statistics?.assistant_stats?.total_assistants ?? 0;
  const botCount = statistics?.token_usage?.bots?.total_bots ?? 0;
  return assistantCount > 0 || botCount > 0;
}

export function resolveEmptyVariant({ statistics, recentActivityTotal }) {
  const hasResources = hasPlatformResources(statistics);
  const hasActivityHistory = (recentActivityTotal ?? 0) > 0;

  if (!hasResources && !hasActivityHistory) {
    return 'onboarding';
  }

  return 'quietPeriod';
}

export function hasNonZeroValues(data, keys) {
  if (!data || data.length === 0) return false;
  return data.some((row) =>
    keys.some((key) => {
      const value = row[key];
      return Number(value) > 0;
    })
  );
}

export function isAllZeroTimeSeries(data, keys) {
  if (!data || data.length === 0) return false;
  return !hasNonZeroValues(data, keys);
}

function buildQuietDistributionTitle(chartKind, timeRange) {
  const period = typeof timeRange === 'string' ? timeRange : timeRange?.period;
  const periodLabel = getPeriodLabel(period, timeRange);
  if (period === 'today') {
    return chartKind === 'latency' ? 'No model calls today' : 'No usage today';
  }
  if (period === 'yesterday') {
    return chartKind === 'latency' ? 'No model calls yesterday' : 'No usage yesterday';
  }
  if (period === 'lastHour') {
    return chartKind === 'latency' ? 'No model calls in the last hour' : 'No usage in the last hour';
  }
  if (period === 'allTime') {
    return chartKind === 'latency' ? 'No model calls recorded' : 'No usage recorded';
  }
  return chartKind === 'latency'
    ? `No model calls in ${periodLabel}`
    : `No usage in ${periodLabel}`;
}

export function getEmptyStateCopy({ variant, chartKind, period, timeRange }) {
  const resolvedRange = timeRange || (typeof period === 'string' ? { period } : { period: 'last7days' });
  const resolvedPeriod = resolvedRange.period;
  if (variant === 'onboarding') {
    const copyByKind = {
      requests: {
        title: 'No usage yet',
        description: 'Create an assistant and send your first request to see activity here.',
      },
      tokens: {
        title: 'No usage yet',
        description: 'Create an assistant and send your first request to see token usage here.',
      },
      cloudSpend: {
        title: 'No cloud spend yet',
        description: 'Cloud LLM usage costs will appear here once models are invoked.',
      },
      pie: {
        title: 'No assistant usage yet',
        description: 'Start chatting with an assistant to see usage distribution here.',
      },
      latency: {
        title: 'No model calls yet',
        description: 'Usage will appear here once models are invoked.',
      },
    };
    return copyByKind[chartKind] || copyByKind.requests;
  }

  if (chartKind === 'pie' || chartKind === 'latency') {
    return {
      title: buildQuietDistributionTitle(chartKind, resolvedRange),
      tone: 'subtle',
    };
  }

  const periodLabel = getPeriodLabel(resolvedPeriod, resolvedRange);
  const quietByKind = {
    requests: {
      title: resolvedPeriod === 'today' ? 'No activity today' : `No activity in ${periodLabel}`,
      description: `No requests recorded in ${periodLabel}.`,
    },
    tokens: {
      title: resolvedPeriod === 'today' ? 'No token usage today' : `No token usage in ${periodLabel}`,
      description: `No token usage recorded in ${periodLabel}.`,
    },
    cloudSpend: {
      title: resolvedPeriod === 'today' ? 'No cloud spend today' : `No cloud spend in ${periodLabel}`,
      description: `No cloud spend recorded in ${periodLabel}.`,
    },
  };

  return quietByKind[chartKind] || quietByKind.requests;
}

export function prepareTimeSeriesChart({
  rawData,
  period,
  timeRange,
  statistics,
  recentActivityTotal,
  timeSeries,
}) {
  const { valueKeys, createBucket } = timeSeries;
  const resolvedRange = timeRange || (typeof period === 'string' ? { period } : { period: 'last7days' });
  const merged = mergeTimeSeriesData(resolvedRange, rawData, createBucket);
  const hasData = hasNonZeroValues(merged, valueKeys);

  if (hasData) {
    return {
      data: merged,
      emptyState: null,
      variant: null,
    };
  }

  const variant = resolveEmptyVariant({ statistics, recentActivityTotal });

  if (variant === 'quietPeriod' && merged.length > 0) {
    return {
      data: merged,
      emptyState: null,
      variant,
    };
  }

  return {
    data: [],
    emptyState: { variant },
    variant,
  };
}
